/**
 * External EMR patients in the Patient Manager: shells, the census refresh,
 * and the patient-scoped import.
 *
 * Thin wrappers over the generated SDK. Every call passes
 * `throwOnError: true`: the generated client resolves a 4xx/5xx as
 * `{ data: undefined, error }` by default, which would read as an empty
 * success.
 *
 * Pure helpers below decide what a card says. Components read those, never
 * the raw state words.
 */
import {
  cancelPatientExternalEmrImportRoutePatientsPatientIdExternalEmrImportDelete,
  getExternalEmrImportReadinessPatientsExternalEmrImportReadinessGet,
  getExternalEmrSummaryPatientsExternalEmrSummaryGet,
  getPatientExternalEmrImportPatientsPatientIdExternalEmrImportGet,
  putExternalEmrPlannedChartsFromPatientsPatientIdExternalEmrPlannedChartsFromPut,
  refreshPatientExternalEmrImportPatientsPatientIdExternalEmrImportRefreshPost,
  startPatientExternalEmrImportPatientsExternalEmrImportStartPost,
  syncExternalEmrCensusPatientsExternalEmrCensusPost,
  listEpisodesForPatient,
  setEpisodeRogerChartsFrom as setEpisodeRogerChartsFromRoute,
} from './api/sdk.gen';
import type {
  EpisodeRogerChartsFromResult,
  EpisodeTakeoverCandidate,
  ExternalEmrCensusSyncStart,
  ExternalEmrImportReadiness,
  ExternalEmrPatientState,
  ExternalEmrRefreshResult,
  ExternalEmrSummary,
  GetExternalEmrSummaryPatientsExternalEmrSummaryGetData,
  GetPatientsPatientsGetData,
  PatientExternalEmrImportStartResult,
  PatientResponse,
} from './api/types.gen';
import {
  normalizeImportStatus,
  type ExternalEmrImportStatus,
} from './externalEmrImportStatus';
import { parseISOToLocalDate } from '../utils/dateUtils';

export type {
  ExternalEmrCensus,
  ExternalEmrCensusSync,
  ExternalEmrCensusSyncResult,
  ExternalEmrCensusSyncStart,
  ExternalEmrImportReadiness,
  ExternalEmrImportStatus,
  ExternalEmrMovePlan,
  ExternalEmrPatientState,
  ExternalEmrRefreshResult,
  ExternalEmrRenderCounts,
  ExternalEmrStatusCounts,
  ExternalEmrSummary,
  PatientExternalEmrImportStartResult,
} from './api/types.gen';

/** The External EMR view's list filter on `GET /patients/`. */
export type ExternalEmrListFilter = NonNullable<
  NonNullable<GetPatientsPatientsGetData['query']>['external_emr']
>;

/** The External EMR table's status filter on `GET /patients/`. */
export type ExternalEmrListStatusFilter = NonNullable<
  NonNullable<GetPatientsPatientsGetData['query']>['external_emr_status']
>;

// =============================================================================
// Requests
// =============================================================================

export async function getExternalEmrSummary(
  query: GetExternalEmrSummaryPatientsExternalEmrSummaryGetData['query'] = {}
): Promise<ExternalEmrSummary> {
  const response = await getExternalEmrSummaryPatientsExternalEmrSummaryGet({
    query,
    throwOnError: true,
  });
  return response.data;
}

/**
 * Whether the agency's stored External EMR login and consent can carry an
 * import now, with the sentence to show when they cannot.
 */
export async function getExternalEmrImportReadiness(): Promise<ExternalEmrImportReadiness> {
  const response =
    await getExternalEmrImportReadinessPatientsExternalEmrImportReadinessGet({
      throwOnError: true,
    });
  return response.data;
}

/**
 * Start a census read. It runs in the background on the server; the summary's
 * `census_sync` reports it.
 */
export async function refreshExternalEmrCensus(): Promise<ExternalEmrCensusSyncStart> {
  const response = await syncExternalEmrCensusPatientsExternalEmrCensusPost({
    throwOnError: true,
  });
  return response.data;
}

/**
 * Start imports. One id runs at interactive priority, several at bulk
 * priority (the server decides by count). Each row answers for itself.
 */
export async function startExternalEmrPatientImports(
  patientIds: string[]
): Promise<PatientExternalEmrImportStartResult[]> {
  const response =
    await startPatientExternalEmrImportPatientsExternalEmrImportStartPost({
      body: { patient_ids: patientIds },
      throwOnError: true,
    });
  return response.data ?? [];
}

/**
 * Refresh an imported chart from the External EMR: what is new there is
 * added, untouched rows are brought forward, rows edited in Roger are left
 * as saved. One chart, interactive priority. Refusals come back in the row.
 */
export async function refreshExternalEmrPatient(
  patientId: string
): Promise<PatientExternalEmrImportStartResult> {
  const response =
    await refreshPatientExternalEmrImportPatientsPatientIdExternalEmrImportRefreshPost(
      {
        path: { patient_id: patientId },
        throwOnError: true,
      }
    );
  return response.data;
}

export async function getPatientExternalEmrImport(
  patientId: string
): Promise<ExternalEmrImportStatus> {
  const response =
    await getPatientExternalEmrImportPatientsPatientIdExternalEmrImportGet({
      path: { patient_id: patientId },
      throwOnError: true,
    });
  return normalizePatientImportStatus(response.data);
}

export async function cancelPatientExternalEmrImport(
  patientId: string
): Promise<ExternalEmrImportStatus> {
  const response =
    await cancelPatientExternalEmrImportRoutePatientsPatientIdExternalEmrImportDelete(
      {
        path: { patient_id: patientId },
        throwOnError: true,
      }
    );
  return normalizePatientImportStatus(response.data);
}

/** Store the day Roger charts from, planned on the census row; null clears it. */
export async function setPlannedRogerChartsFrom(
  patientId: string,
  day: string | null
): Promise<string | null> {
  const response =
    await putExternalEmrPlannedChartsFromPatientsPatientIdExternalEmrPlannedChartsFromPut(
      {
        path: { patient_id: patientId },
        body: { roger_charts_from_planned: day },
        throwOnError: true,
      }
    );
  return response.data.roger_charts_from_planned ?? null;
}

/**
 * The chart's Take Over candidate, as the chart page reads it: the latest
 * active imported period on the current admission. Null when Take Over is
 * not open on the chart.
 */
export async function getTakeOverCandidate(
  patientId: string
): Promise<EpisodeTakeoverCandidate | null> {
  const response = await listEpisodesForPatient({
    path: { patient_id: patientId },
    throwOnError: true,
  });
  return response.data.takeover_candidate ?? null;
}

/**
 * Change the day Roger charts from after Take Over, on the episode that
 * moved the chart. Returns the stored day and the visits it handed over.
 */
export async function setEpisodeRogerChartsFrom(
  episodeId: string,
  day: string
): Promise<EpisodeRogerChartsFromResult> {
  const response = await setEpisodeRogerChartsFromRoute({
    path: { episode_id: episodeId },
    body: { roger_charts_from: day },
    throwOnError: true,
  });
  return response.data;
}

/** The intake normalization, with `renders` and `history` kept (null when absent). */
export function normalizePatientImportStatus(
  raw: Partial<ExternalEmrImportStatus> | null | undefined,
  receivedAtMs: number = Date.now()
): ExternalEmrImportStatus {
  return {
    ...normalizeImportStatus(raw, receivedAtMs),
    renders: raw?.renders ?? null,
    refresh: raw?.refresh ?? false,
    refresh_result: raw?.refresh_result ?? null,
    history: raw?.history ?? null,
  };
}

// =============================================================================
// What a card says (pure)
// =============================================================================

export type ExternalEmrCardStatus =
  | 'ready'
  | 'mid_episode'
  | 'ending_soon'
  | 'ready_to_cut_over'
  | 'left_before_end'
  | 'queued'
  | 'importing'
  | 'failed'
  | 'imported'
  | 'refreshing'
  | 'refresh_failed';

/** A shell at rest (no import moving): what the census says about it. */
function restingShellStatus(
  external: Pick<ExternalEmrPatientState, 'ready' | 'census_status'>
): ExternalEmrCardStatus {
  switch (external.census_status) {
    case 'mid_episode':
      return 'mid_episode';
    case 'ending_soon':
      return 'ending_soon';
    case 'ended':
    case 'left_census_after_end':
      return 'ready';
    case 'recertified_in_external_emr':
      // The episode ended and the External EMR built the next period.
      return 'ready_to_cut_over';
    case 'left_census_before_end':
      return 'left_before_end';
    default:
      return external.ready ? 'ready' : 'mid_episode';
  }
}

/**
 * One status per card. A live import wins over the stored state, so a card
 * reads Imported the moment its poll sees completion, before the list reloads.
 * A canceled import returns the shell to its census status.
 *
 * Completion counts only with a chart behind it (`patient_id`). A completed
 * row with none only joined an earlier run, or its chart was deleted since:
 * the shell rests where it was and can be imported again.
 *
 * A refresh row (`live.refresh`) runs on a chart already in Roger: it reads
 * Refreshing while it runs and Imported once it completes; a failed refresh
 * keeps the chart Imported and says the refresh failed.
 */
export function externalEmrCardStatus(
  external: Pick<ExternalEmrPatientState, 'state' | 'ready' | 'census_status'>,
  live: ExternalEmrImportStatus | null | undefined
): ExternalEmrCardStatus {
  const refresh = !!live?.refresh;
  switch (live?.state) {
    case 'queued':
      return 'queued';
    case 'running':
      return refresh ? 'refreshing' : 'importing';
    case 'completed':
      if (live.patient_id) return 'imported';
      break;
    case 'failed':
      if (external.state === 'shell') return 'failed';
      if (refresh) return 'refresh_failed';
      break;
    default:
      break;
  }
  if (external.state === 'imported') return 'imported';
  return restingShellStatus(external);
}

const MOVING_OR_DONE: ReadonlySet<ExternalEmrCardStatus> = new Set([
  'queued',
  'importing',
  'imported',
  'refreshing',
  'refresh_failed',
]);

/**
 * The shell can be started from this card: any resting shell, or a failed
 * one. Whether the start asks first is keyed on `ready`, not on this.
 */
export function canStartFromStatus(status: ExternalEmrCardStatus): boolean {
  return !MOVING_OR_DONE.has(status);
}

/**
 * The imported chart can be refreshed from this card: at rest, or after a
 * refresh that failed. Never while a row is queued or running.
 */
export function canRefreshFromStatus(status: ExternalEmrCardStatus): boolean {
  return status === 'imported' || status === 'refresh_failed';
}

/** Queued or running, for either an import or a refresh. */
export function isMovingStatus(status: ExternalEmrCardStatus): boolean {
  return (
    status === 'queued' || status === 'importing' || status === 'refreshing'
  );
}

/**
 * How long a finished refresh's result stays on the card and the chart. A
 * week covers the cutover: import, add the episode, refresh again once the
 * External EMR exports the recert. After that the line fades; the Import
 * report on the chart header keeps every run (Daniel, 2026-10-09: "Fade from
 * view, keep in records").
 */
export const REFRESH_RESULT_SHOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * A refresh that finished within the window, with what it did. Null until
 * it finishes, and null again once it has faded. A result with no finish
 * time cannot be aged, so it shows.
 */
export function completedRefreshResult(
  live: ExternalEmrImportStatus | null | undefined,
  nowMs: number
): ExternalEmrRefreshResult | null {
  if (!live?.refresh || live.state !== 'completed') return null;
  const finishedAt = live.finished_at ? Date.parse(live.finished_at) : NaN;
  if (
    !Number.isNaN(finishedAt) &&
    nowMs - finishedAt > REFRESH_RESULT_SHOWN_MS
  ) {
    return null;
  }
  return live.refresh_result ?? null;
}

/**
 * Whether a refresh row is worth a line on the chart: queued, running, or
 * finished within the window.
 */
export function refreshStillShowing(
  live: ExternalEmrImportStatus | null | undefined,
  nowMs: number
): boolean {
  if (!live?.refresh) return false;
  if (live.state === 'queued' || live.state === 'running') return true;
  if (live.state === 'failed') return true;
  return completedRefreshResult(live, nowMs) !== null;
}

/** Print renders are still queued or running for the imported chart. */
export function rendersPending(
  status: Pick<ExternalEmrImportStatus, 'renders'> | null | undefined
): boolean {
  const renders = status?.renders;
  if (!renders) return false;
  return (renders.queued ?? 0) > 0 || (renders.running ?? 0) > 0;
}

/**
 * The chart landed its recent episodes first, and the admission's earlier
 * episodes are still queued or running behind it.
 */
export function historyPending(
  status: Pick<ExternalEmrImportStatus, 'history'> | null | undefined
): boolean {
  return status?.history === 'arriving';
}

/**
 * The earlier episodes did not come over: the chart holds only the recent
 * ones until someone runs the history again.
 */
export function historyFailed(
  status: Pick<ExternalEmrImportStatus, 'history'> | null | undefined
): boolean {
  return status?.history === 'failed';
}

/**
 * Every render pass has settled and some documents did not come over.
 * Said only after nothing is left queued or running.
 */
export function rendersUnrecovered(
  status: Pick<ExternalEmrImportStatus, 'renders'> | null | undefined
): boolean {
  if (!status?.renders || rendersPending(status)) return false;
  return (status.renders.unrecovered ?? 0) > 0;
}

/**
 * The imported chart's last episode has ended: the next step is the recert
 * episode. `latestEpisodeEnd` and `today` are `YYYY-MM-DD` calendar dates.
 */
export function lastEpisodeHasEnded(
  latestEpisodeEnd: string | null | undefined,
  today: string
): boolean {
  const end = parseISOToLocalDate(latestEpisodeEnd ?? '');
  const now = parseISOToLocalDate(today);
  if (!end || !now) return false;
  return end.getTime() < now.getTime();
}

/** `external_emr` from a patient read. Null for a native patient. */
export function externalEmrOf(
  patient: Pick<PatientResponse, 'external_emr'> | null | undefined
): ExternalEmrPatientState | null {
  return patient?.external_emr ?? null;
}

export type AddEpisodeUrlDecision =
  'wait' | 'open' | 'refuse_shell' | 'refuse_blocked';

/**
 * What `?addEpisode=1` does on the chart. It waits until this patient and its
 * episodes have loaded, so the answer is about them. A shell has no admission
 * to add to; a blocked chart says why. Anything but `wait` drops the flag.
 */
export function addEpisodeUrlDecision({
  patientLoaded,
  episodesSettled,
  isShell,
  canAddEpisode,
}: {
  patientLoaded: boolean;
  episodesSettled: boolean;
  isShell: boolean;
  canAddEpisode: boolean;
}): AddEpisodeUrlDecision {
  if (!patientLoaded || !episodesSettled) return 'wait';
  if (isShell) return 'refuse_shell';
  return canAddEpisode ? 'open' : 'refuse_blocked';
}
